import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useQueryClient } from "@tanstack/react-query";

import { api } from "../lib/api";
import { contractTypes } from "../lib/contract-types";
import { useUser } from "../components/auth-provider";
import { ClauseCell } from "../components/clause-cell";
import { Button } from "../components/ui/button";
import { Skeleton } from "../components/ui/skeleton";
import { IPlaybookClause } from "../hooks/playbook-clause";
import { useSelectContract } from "../hooks/select-contract";
import { clearAnalysis, setPlaybookMetadata } from "../../store/playbook-analysis-slice";
import { RootState } from "../../store/store";

import Dropdown from "../../utils/Dropdown";
import NoDocument from "../../utils/components/NoDocument";
import ErrorComponent from "../../utils/components/ErrorComponent";
import { Loader } from "../components/loader";

async function fetchPlaybookClauses({ id }: { id: string }) {
	const clauses: any = await api.get("/api/v1/playbook/clauses", {
		params: {
			playbook_id: id,
		},
	});

	return clauses;
}

export function ManagePlaybook() {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const qc = useQueryClient();

	const { user } = useUser();

	const file = useSelector((state: RootState) => state.files);

	const [error, setError] = useState<string | undefined>();
	const [playbooks, setPlaybooks] = useState<{ label: string; value: string }[]>([]);
	const [clauses, setClauses] = useState<IPlaybookClause[]>([]);
	const [activePlaybook, setActivePlaybook] = useState<{ label: string; value: string } | undefined>();
	const [loading, setLoading] = useState<boolean>(true);
	const [loadingClauses, setLoadingClauses] = useState<boolean>(false);

	const selectContract = useSelectContract();

	async function handleSelectPlaybook({ id }: { id: string }) {
		try {
			setLoadingClauses(true);
			setError(undefined);

			const active: any = await api.post("/api/v1/playbook/activate-playbook", {
				contractType: file.fileType.value,
				playbookId: id,
			});

			if (!active.success) {
				setError(active.message ?? "Failed to change the playbook");
				return;
			}

			dispatch(setPlaybookMetadata({ id }));

			const clauses: any = await fetchPlaybookClauses({ id });
			if (!clauses.success) {
				setError(clauses.message ?? "Failed to fetch the playbook clauses");
				return;
			}
			setClauses(clauses.data);
		} catch (err) {
			setError("Failed to select playbook");
		} finally {
			setLoadingClauses(false);
		}
	}

	useEffect(() => {
		async function fetchPlaybookMetadata() {
			setLoading(true);
			const metadata: any = await api.get("/api/v1/playbook/playbooks-metadata");
			if (!metadata.success) {
				setLoading(false);
				setError(metadata.message ?? "Fetching playbooks metadata failed");
				return;
			}

			const filteredPlaybooks = metadata.data
				.filter((p) => p.contract_type === file.fileType.value)
				.map((p) => ({
					...p,
					label: p.playbook_name,
					value: p.playbook_id,
				}));

			setPlaybooks(filteredPlaybooks);

			const activePlaybook = filteredPlaybooks.find((p) => p.active);
			if (!activePlaybook) {
				setLoading(false);
				setError("No active playbook found");
				return;
			}

			dispatch(setPlaybookMetadata({ id: activePlaybook.value }));
			setActivePlaybook({ value: activePlaybook.value, label: activePlaybook.label });

			const clauses: any = await fetchPlaybookClauses({ id: activePlaybook.value });
			if (!clauses.success) {
				setLoading(false);
				setError(clauses.message ?? "Failed to fetch the playbook clauses");
				return;
			}
			setClauses(clauses.data);
			setLoading(false);
		}
		fetchPlaybookMetadata();
	}, [file.fileId, file.fileType?.value, dispatch]);

	if (!file?.fileId) {
		return <NoDocument />;
	}

	if (loading || selectContract.isPending) {
		return <Loader />;
	}

	if (error || selectContract.error) {
		return <ErrorComponent open message={error ?? selectContract.error} />;
	}

	return (
		<div className="flex flex-col p-3 gap-3 max-h-screen">
			<h2 className="text-xl font-medium">Manage Playbooks</h2>

			<div className="flex flex-col gap-1">
				<p className="text-sm">Contract Type</p>
				<Dropdown
					options={contractTypes}
					initialValue={file.fileType}
					onSelect={(data) => {
						selectContract.mutate({
							name: data.label,
							code: data.value,
							userId: user.id,
							fileId: file.fileId,
						});
					}}
				/>
			</div>

			<div className="flex flex-col gap-1">
				<p className="text-sm font-medium">Choose Playbook</p>
				<Dropdown
					options={playbooks}
					initialValue={activePlaybook}
					onSelect={(data) => {
						handleSelectPlaybook({ id: data.value });
					}}
				/>
			</div>

			<div className="overflow-auto flex-1 border rounded-lg grid gap-2 p-3 mb-24 overscroll-none">
				{loadingClauses ? (
					<div className="flex flex-col gap-2">
						{Array.from({ length: 10 }).map((_, idx) => (
							<Skeleton key={idx} className="h-10 w-full rounded-md" />
						))}
					</div>
				) : (
					<>
						{clauses.map((clause, idx) => (
							<ClauseCell key={idx} clause={clause} />
						))}
					</>
				)}
			</div>

			<div className="gap-3 fixed bottom-0 left-14 right-0 border-t-2 px-3 py-2 bg-background">
				<div className="grid gap-3">
					<Button
						size="sm"
						onClick={() => {
							dispatch(clearAnalysis());
							qc.setQueryData(["analyze-playbook"], null);
							navigate("/analyze");
						}}
					>
						Start Analysis
					</Button>

					<Button size="sm" onClick={() => navigate("/upload")} variant="secondary">
						Upload New Playbook
					</Button>
				</div>
			</div>
		</div>
	);
}
